/**
 * 历史包裹查询（总部 / 包裹查询 / 历史包裹查询）。
 *
 * 结构完全仿照 CentralParcelsPage，三处差异：
 * 1. 查询走 useCentralParcelHistoryQuery（getOrderListingHistory，数据源 order_listing_history 索引）
 * 2. 隐藏「异常信息」整组筛选与异常状态列——异常状态取自 exception_order 表，历史链路不读该表
 * 3. 详情弹窗传 isHistory + hideEditActions：归档订单只读，不提供任何编辑入口
 *
 * 只能查到已归档订单（妥投满 6 个月后才归档）；在途与近期订单请用「包裹查询」。
 */
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

import {getUserInfo, hasHeadquartersAccess, hasTrackExportAccess} from "../../../utils/auth";
import {useHashState} from 'utils/hashState';
import {ParcelInitialValue} from "../../../config/filterOptions";
import {ParcelColumns} from "../../delivery/parcels/components/ParcelColumns";
import {OrderType} from "../../../graphql/types/OrderType";
import {OrderFiltersType} from "../../../graphql/types/OrderFiltersType";
import {useCentralParcelHistoryQuery} from "./hooks/useCentralParcelHistoryQuery";
import CentralParcelDetailDrawer from "./components/CentralParcelDetailDrawer";
import CentralParcelFilter from "./components/CentralParcelFilter";
import {getLastWeekDateTimeRange} from "../../../utils/dateTimeUtils";

// 历史包裹查询：订单时间默认置空（null = 显式为空、不回填默认最近一周，也不作为查询条件），
// 其余筛选沿用 ParcelInitialValue。与普通包裹查询（默认最近一周）不同，故单独定义初始值。
const HISTORY_INITIAL_VALUE = {...ParcelInitialValue, orderTime: null};

const CentralParcelHistoryPage: React.FC = () => {
    const {t} = useTranslation();
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');
    const [tempFilters, setTempFilters] = useHashState(HISTORY_INITIAL_VALUE);
    const [filters, setFilters] = useHashState(HISTORY_INITIAL_VALUE);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // 当前页显示用的勾选 key（受控传给 Table）
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    // 跨分页持久化的勾选 key 集合
    const [persistentSelection, setPersistentSelection] = useState<React.Key[]>([]);
    // 跨分页缓存的选中行数据，用于批量操作
    const [selectedRowMap, setSelectedRowMap] = useState<Map<React.Key, OrderType>>(new Map());
    const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);
    const headquartersFlag = hasHeadquartersAccess();
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | null>(null);
    
    // 默认时间范围不写入 URL：只在页面加载时计算一次，避免每次 render 都生成新时间导致无限 refetch
    const defaultOrderTime = useMemo(() => getLastWeekDateTimeRange(), []);

    // 使用自定义hook获取数据
    const getEffectiveOrderTime = (orderTime: any) => {
        // null：用户手动清空（点击叉号），保持为空，不回填默认值
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2 &&
            orderTime[0] !== null &&
            orderTime[1] !== null;
        return isValid ? orderTime : defaultOrderTime;
    };

    const effectiveFilters = {
        ...filters,
        orderTime: getEffectiveOrderTime(filters.orderTime)
    };

    const {
        parcelData,
        loading,
        refetch,
        totalElements,
        accessiblePages,
        buildQueryVariables
    } = useCentralParcelHistoryQuery(effectiveFilters, currentPage, pageSize, sortField, sortDirection);

    const handleClearSelection = () => {
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedRowMap(new Map());
        setSelectedRows([]);
    };

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: OrderType[]) => {
        // 1. 先更新当前页的受控选中 key
        setSelectedRowKeys(newSelectedRowKeys);
        // 2. 基于当前页的数据，合并到跨页持久化选择
        setPersistentSelection(prevKeys => {
            const nextKeys = [...prevKeys];

            // 只处理当前页的数据，避免影响其它页的选择
            parcelData.forEach((order) => {
                const key: React.Key = order.trackingNumber || order.id;
                const isSelectedOnPage = newSelectedRowKeys.includes(key);

                if (isSelectedOnPage) {
                    if (!nextKeys.includes(key)) {
                        nextKeys.push(key);
                    }
                } else {
                    const idx = nextKeys.indexOf(key);
                    if (idx > -1) {
                        nextKeys.splice(idx, 1);
                    }
                }
            });

            return nextKeys;
        });

        // 3. 同步缓存选中行的数据（仅更新当前页相关的记录）
        setSelectedRowMap(prevMap => {
            const nextMap = new Map(prevMap);

            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                const isSelectedOnPage = newSelectedRowKeys.includes(key);

                if (isSelectedOnPage) {
                    nextMap.set(key, order);
                } else {
                    nextMap.delete(key);
                }
            });

            return nextMap;
        });
    };

    const handleResetFilters = () => {
        // updateHashState 会保留 undefined 字段，encodeHash 会过滤它们，URL hash 中不会包含
        setTempFilters(HISTORY_INITIAL_VALUE);
        setFilters(HISTORY_INITIAL_VALUE);
    };

    const handleFilter = () => {
        // 直接使用 tempFilters 的值来更新 filters，确保 hashState 同步
        // 需要明确设置所有字段，包括要删除的字段（设为 undefined）
        // 这样 updateHashState 才能包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
        const newFilters: any = {...tempFilters};
        // 确保 filters 中所有可能的字段都被包含，即使值是 undefined 或 null
        // 如果 tempFilters 中某个字段不存在，但 filters 中有，需要明确设置为 undefined
        // 这样可以确保旧字段被包含在更新中，encodeHash 会过滤它们
        const allKeys = new Set([...Object.keys(tempFilters), ...Object.keys(filters)]);
        allKeys.forEach(key => {
            if (!(key in newFilters)) {
                newFilters[key] = undefined;
            }
        });
        // updateHashState 会保留 undefined 字段，encodeHash 会过滤它们，URL hash 中不会包含已删除的字段
        setFilters(newFilters);
        setCurrentPage(1);
    };

    // 当筛选条件或排序变化时清空所有选中状态（跨页选择也一并清空）
    useEffect(() => {
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedRowMap(new Map());
        setSelectedRows([]);
    }, [filters, sortField, sortDirection]);

    // 每次数据刷新后，根据持久化选择恢复当前页的勾选状态
    useEffect(() => {
        if (loading || !parcelData || parcelData.length === 0) {
            return;
        }

        const keysOnCurrentPage: React.Key[] = [];
        parcelData.forEach(order => {
            const key: React.Key = order.trackingNumber || order.id;
            if (persistentSelection.includes(key)) {
                keysOnCurrentPage.push(key);
            }
        });

        setSelectedRowKeys(keysOnCurrentPage);
    }, [parcelData, loading, persistentSelection]);

    // 数据刷新时，把当前页中已持久选中的行补齐到缓存 Map 中
    useEffect(() => {
        if (loading || !parcelData || parcelData.length === 0) {
            return;
        }

        setSelectedRowMap(prevMap => {
            const nextMap = new Map(prevMap);

            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                if (persistentSelection.includes(key)) {
                    nextMap.set(key, order);
                }
            });

            return nextMap;
        });
    }, [parcelData, loading, persistentSelection]);

    // 统一从 Map 中导出选中行数组，供下游操作使用
    useEffect(() => {
        setSelectedRows(Array.from(selectedRowMap.values()));
    }, [selectedRowMap]);

    const showParcelDetail = (record: OrderType) => {
        setOrderId(record.id);
        setDetailVisible(true);
    };
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 筛选条件 */}
                <CentralParcelFilter
                hideExceptionFilters
                hideActionButtons
                isHistoryDownload
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    selectedRows={selectedRows}
                    onClearSelection={handleClearSelection}
                    queryTotalElements={totalElements}
                />

                {/* 订单列表 */}
                <ParcelColumns
                hiddenColumnKeys={['isAbnormal', 'isHasBeenSetException']}
                    parcelData={parcelData}
                    showParcelDetail={showParcelDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    onSortChange={(field, direction) => {
                        setSortField(field ?? null);
                        setSortDirection(direction ?? null);
                        setCurrentPage(1);
                    }}
                    sortField={sortField}
                    sortDirection={sortDirection}
                />

                {/* 分页：total 显示真实总数，maxPages 限制最大可翻页数（深度翻页保护）*/}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    maxPages={accessiblePages}
                />

                {/* 包裹详情抽屉组件 */}
                <CentralParcelDetailDrawer
                isHistory
                hideEditActions
                    showInterceptionExemptionAlert={true}
                    visible={detailVisible}
                    onClose={() => setDetailVisible(false)}
                    orderId={orderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                    onRefreshList={() => refetch(buildQueryVariables())}
                    showExternalTrack={true}
                />
            </div>
    );
};

export default CentralParcelHistoryPage;